import React, { useState, useEffect, useMemo } from "react";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getProductHistory } from "../../../common/services/products/productService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const ProductHistory = ({ productId }) => {
  const { t } = useTranslation(["products"]);
  const [productHistory, setProductHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch product history data when component mounts or ID changes
  useEffect(() => {
    const fetchHistory = async () => {
      if (!productId) return;

      try {
        setIsLoading(true);
        const data = await getProductHistory(productId);
        const formattedData = mapToViewModel(data);
        setProductHistory(formattedData);
      } catch (error) {
        console.error("Error fetching product history:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, [productId]);

  // Map API data to view model with formatted dates
  const mapToViewModel = (data) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return data.map((product) => ({
      id: product.id,
      name: product.name,
      shortName: product.shortName,
      commodity: product.commodityName,
      active: product.active,
      activeString: product.active,
      updatedByUserAccountId: product.createdByUserAccountId,
      updatedBy:
        product.updatedByUserAccountFirstName +
        " " +
        product.updatedByUserAccountLastName,
      updatedDate: new Date(product.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      writeDate: new Date(product.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: product.eventId,
    }));
  };

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "id",
        headerName: t("RecordID"),
        sortable: true,
        filter: true,
        sort: "desc",
        width: 100,
      },
      {
        field: "name",
        headerName: t("ProductName"),
        sortable: true,
        filter: true,
        flex: 2,
      },
      {
        field: "shortName",
        headerName: t("ProductShortName"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "commodity",
        headerName: t("Commodity"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "active",
        headerName: t("Active"),
        sortable: true,
        filter: true,
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      {
        field: "updatedBy",
        headerName: t("UpdatedBy"),
        sortable: true,
        filter: true,
        flex: 2,
      },
      {
        field: "updatedDate",
        headerName: t("UpdateDate"),
        sortable: true,
        filter: true,
        width: 180,
      },
      {
        field: "writeDate",
        headerName: t("WriteDate"),
        sortable: true,
        filter: true,
        width: 180,
      },
      {
        field: "eventId",
        headerName: t("EventID"),
        sortable: true,
        filter: true,
        width: 100,
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 80,
      autoHeight: true,
    }),
    []
  );

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container">
      <div className="mb-3">
        {t("ProductID")} {productId}
      </div>
      <ThemeableAgGrid
        className="table-extra-small"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={productHistory}
        getRowId={(params) => params.data.id.toString()}
        animateRows={true}
        domLayout="autoHeight"
        pagination={true}
        paginationPageSize={10}
      />
    </div>
  );
};

export default ProductHistory;

